import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { fetchDashboardMetrics } from '../apis/dashboardApi';

export function Dashboard() {
  const [aocStatusMismatchesCount, setAocStatusMismatchesCount] = useState(null);
  const [deliveredMissingDeliveryDateCount, setDeliveredMissingDeliveryDateCount] = useState(null);
  const [openAocsMismatchCount, setOpenAocsMismatchCount] = useState(null);
  const [dashboardError, setDashboardError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setDashboardError(false);
    fetchDashboardMetrics()
      .then((data) => {
        if (!cancelled) {
          setAocStatusMismatchesCount(data.aoc_status_mismatches_count);
          setDeliveredMissingDeliveryDateCount(data.delivered_missing_delivery_date_count);
          setOpenAocsMismatchCount(data.open_aocs_mismatch_count);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDashboardError(true);
          setAocStatusMismatchesCount(null);
          setDeliveredMissingDeliveryDateCount(null);
          setOpenAocsMismatchCount(null);
        }
      });
    return () => { cancelled = true; };
  }, []);

  const attentionItems = [
    {
      title: 'AOC Status Mismatches',
      count: 23,
      borderColor: '#e74c3c',
      iconClass: 'fa fa-exclamation-triangle',
      iconColor: '#e74c3c',
      link: '/veevaReconcile/issues?activeOnly=true&recon_status=Status%20Mismatch',
    },
    {
      title: 'Missing Delivery Date',
      count: 8,
      borderColor: '#f39c12',
      iconClass: 'fa fa-calendar-times-o',
      iconColor: '#f39c12',
      link: '/veevaReconcile/issues?activeOnly=true&recon_status=Missing%20Delivery%20Date',
    },
    {
      title: 'Failed Push/Pull Events',
      count: 5,
      borderColor: '#e67e22',
      iconClass: 'fa fa-refresh',
      iconColor: '#e67e22',
      link: '/veevaReconcile/issues?tab=operational-errors',
    },
    {
      title: 'Open AOCs Mismatch',
      borderColor: '#0098DB',
      iconClass: 'fa fa-files-o',
      iconColor: '#0098DB',
      link: '/veevaReconcile/issues?activeOnly=true&recon_status=Status%20Mismatch',
    },
  ];

  const activityMetrics = [
    { label: 'SOT Pulled', count: '1,247', trend: '+5.2%', trendUp: true },
    { label: 'Orders Generated (Veeva)', count: '892', trend: '+3.1%', trendUp: true },
    { label: 'Orders Generated (SRF)', count: '355', trend: '-1.2%', trendUp: false },
    { label: 'SRFs Pushed', count: '347', trend: '+2.8%', trendUp: true },
    { label: 'AOCs Pulled', count: '1,198', trend: '+4.5%', trendUp: true },
    { label: 'AOCs Pushed', count: '1,156', trend: '+3.9%', trendUp: true },
  ];

  return (
    <div>
      {/* Attention Needed */}
      <article className="card">
        <header>
          <i className="fa fa-warning"></i>
          <label>Attention Needed</label>
        </header>
        <div className="body">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
            {attentionItems.map((item, index) => {
              const isAoc = index === 0;
              const isMissing = index === 1;
              const isOpen = index === 3;
              const displayCount = isAoc
                ? (dashboardError || aocStatusMismatchesCount === null ? '—' : Math.floor(aocStatusMismatchesCount))
                : isMissing
                  ? (dashboardError || deliveredMissingDeliveryDateCount === null ? '—' : Math.floor(deliveredMissingDeliveryDateCount))
                  : isOpen
                    ? (dashboardError || openAocsMismatchCount === null ? '—' : Math.floor(openAocsMismatchCount))
                    : item.count ?? '—';
              return (
                <div
                  key={item.title}
                  style={{
                    border: '1px solid #ddd',
                    borderLeft: `4px solid ${item.borderColor}`,
                    borderRadius: '4px',
                    padding: '16px',
                    backgroundColor: '#fff',
                    transition: 'box-shadow 0.2s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 3px 6px rgba(0,0,0,0.16)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                    <i className={item.iconClass} style={{ fontSize: '20px', color: item.iconColor }}></i>
                    <span style={{ fontSize: '13px', color: '#666', fontWeight: 600 }}>{item.title}</span>
                  </div>
                  <div style={{ fontSize: '32px', fontWeight: 700, color: '#0039A6', marginBottom: '8px' }}>
                    {displayCount}
                  </div>
                  <Link
                    to={item.link}
                    style={{ fontSize: '13px', color: '#0098DB', fontWeight: 600, textDecoration: 'none' }}
                  >
                    View Issues <i className="fa fa-arrow-right" style={{ fontSize: '11px' }}></i>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </article>

      {/* Activity Summary */}
      <article className="card">
        <header>
          <i className="fa fa-line-chart"></i>
          <label>Activity Summary</label>
        </header>
        <div className="body">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
            {activityMetrics.map((metric) => (
              <div
                key={metric.label}
                style={{
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '16px',
                  backgroundColor: '#fafbfc',
                }}
              >
                <div style={{ fontSize: '13px', color: '#666', marginBottom: '6px', fontWeight: 600 }}>
                  {metric.label}
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '28px', fontWeight: 700, color: '#0039A6' }}>
                    {metric.count}
                  </span>
                  <span style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: metric.trendUp ? '#27ae60' : '#e74c3c',
                  }}>
                    <i className={metric.trendUp ? 'fa fa-arrow-up' : 'fa fa-arrow-down'} style={{ fontSize: '11px', marginRight: '3px' }}></i>
                    {metric.trend}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </article>
    </div>
  );
}
